import AccessAlarmsOutlinedIcon from '@mui/icons-material/AccessAlarmsOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Card,
    Divider,
    Grid,
    Stack,
    Typography,
    useTheme,
    Snackbar,
    Alert,
} from '@mui/material';
import CartContext from 'contexts/CartContext';
import useBoolean from 'hooks/useBoolean';
import ChevronLeft from 'icons/ChevronLeft';
import ShoppingCart from 'icons/ShoppingCart';
import DashboardLayout from 'layouts/Dashboard/DashboardLayout';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import formatCurrency from 'utils/formatCurrency';
import useAuth from 'hooks/useAuth';

const Payment = () => {
    const cart = useContext(CartContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const { user } = useAuth();

    const openSnackBar = useBoolean();

    const isPaymentProcess = useBoolean();

    useEffect(() => {
        const getData = async () => {
            let shouldStop = false;
            await axiosInstance
                .get('/api/prv/member-invoices', {
                    params: {
                        packageType: 'main',
                    },
                })
                .then((res) => {
                    const data = res?.data || [];
                    const pendingInvoice = data.find(
                        (item) => 'pending' === item.status.value,
                    );
                    const paidInvoice = data.find(
                        (item) => 'paid' === item.status.value,
                    );
                    const found = pendingInvoice || paidInvoice;
                    if (found) {
                        cart.setFromInvoice(found);
                        navigate('/dashboard/payment-complete');
                        shouldStop = true;
                    }
                })
                .catch(() => {});

            if (shouldStop) return;

            if (!cart.package) {
                navigate('/dashboard/choose-package');
            }
        };

        getData();
    }, [navigate, cart.package]);

    const handleCreateInvoice = async () => {
        isPaymentProcess.onTrue();

        let values = {
            package_id: cart.package.id,
            workshops: cart.workshops.map((workshop) => workshop.id),
        };

        await axiosInstance
            .post('/api/prv/member-invoices', values)
            .then(() => {
                navigate('/dashboard/payment-complete');
            })
            .catch((err) => {
                console.log(err);
                setSnackbarMessage(err.message || 'Error');
                openSnackBar.onTrue();
            })
            .finally(() => isPaymentProcess.onFalse());
    };

    const subtotal =
        parseInt(cart.sympoPrice) +
            cart.workshops.reduce((acc, cur) => acc + parseInt(cur.price), 0) ||
        0;
    const hasBundleDiscount = cart?.package?.isBundle;
    const hasWorkshopDiscountSelected =
        (cart.workshops || []).some((ws) => ws?.isDiscount) || false;
    const isNurse = user?.profession?.name === 'Nurse';
    let bundleDiscountAmount = hasBundleDiscount
        ? hasWorkshopDiscountSelected && isNurse
            ? cart.sympoPrice
            : cart.bundleDiscount
        : 0;
    const sympoDiscountAmount =
        user?.hasDiscount &&
        !(hasBundleDiscount && hasWorkshopDiscountSelected && isNurse)
            ? Math.floor(cart.sympoPrice * 0.3)
            : 0;
    const totalDiscountAmount = bundleDiscountAmount + sympoDiscountAmount;
    const finalAmount = subtotal - totalDiscountAmount;

    const handleBack = () => {
        if (cart.workshops.length == 0) {
            navigate('/dashboard/choose-package');
        } else {
            navigate('/dashboard/choose-workshop');
        }
    };

    return (
        <>
            <Snackbar
                open={openSnackBar.value}
                autoHideDuration={3000}
                onClose={() => openSnackBar.onFalse()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={'error'}
                    onClose={() => openSnackBar.onFalse()}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <DashboardLayout>
                <Box pt={2} pb={4}>
                    <Box mt={2}>
                        <Button
                            disableRipple
                            startIcon={<ChevronLeft />}
                            variant="contained"
                            size="small"
                            onClick={() => handleBack()}
                        >
                            Back
                        </Button>
                    </Box>
                    <Grid container spacing={3} mt={3}>
                        <Grid item md={7} xs={12}>
                            <Card sx={{ padding: 3 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        mb={2}
                                    >
                                        Your Order Summary
                                    </Typography>
                                    <img
                                        src={
                                            process.env.PUBLIC_URL +
                                            '/logo-semarang.webp'
                                        }
                                        alt="logo"
                                        style={{
                                            maxWidth: '200px',
                                            height: 'auto',
                                        }}
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="h6" color="text.main">
                                        Package Choose
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700}>
                                        {cart.package?.name || '-'}
                                    </Typography>
                                </Box>

                                <Typography
                                    variant="h6"
                                    color="text.main"
                                    sx={{ marginTop: 2 }}
                                >
                                    Package Included
                                </Typography>

                                <Stack spacing={0.5}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginTop: 0,
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight={600}
                                        >
                                            Symposium
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight={700}
                                        >
                                            {formatCurrency(
                                                cart.sympoPrice || 0,
                                            )}
                                        </Typography>
                                    </Box>
                                    {cart?.workshops?.map((item, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="body1"
                                                    fontWeight={600}
                                                >
                                                    {item.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <CalendarMonthOutlinedIcon size="small" />
                                                    {item.date}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <AccessAlarmsOutlinedIcon size="small" />
                                                    {item.time}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="body1"
                                                fontWeight={700}
                                            >
                                                {formatCurrency(
                                                    parseInt(item.price || 0),
                                                )}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>

                                <Divider sx={{ my: 2 }} />

                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={700}
                                    >
                                        Subtotal
                                    </Typography>
                                    <Typography
                                        variant="subtitle1"
                                        color="error.main"
                                        fontWeight={800}
                                    >
                                        {formatCurrency(subtotal)}
                                    </Typography>
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item md={5} xs={12}>
                            <Card sx={{ padding: 3 }}>
                                <Typography
                                    variant="h4"
                                    fontWeight={800}
                                    mb={2}
                                >
                                    Proceed to Payment
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                <Stack spacing={1}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            color="text.main"
                                        >
                                            Subtotal Price
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight={600}
                                        >
                                            {formatCurrency(
                                                cart.sympoPrice +
                                                    cart.totalWorkshopPrice,
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Stack spacing={0.5}>
                                            <Typography
                                                variant="body1"
                                                color="text.main"
                                            >
                                                Discount Applied
                                            </Typography>
                                            {sympoDiscountAmount > 0 && (
                                                <Typography
                                                    variant="body2"
                                                    color="error.main"
                                                >
                                                    - {formatCurrency(sympoDiscountAmount)} (Symposium Discount)
                                                </Typography>
                                            )}
                                            {bundleDiscountAmount > 0 && (
                                                <Typography
                                                    variant="body2"
                                                    color="error.main"
                                                >
                                                    - {formatCurrency(bundleDiscountAmount)} (Bundle Discount)
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Typography
                                            variant="h6"
                                            fontWeight={800}
                                            color={
                                                totalDiscountAmount > 0
                                                    ? theme.palette.success.main
                                                    : theme.palette.text.primary
                                            }
                                        >
                                            {formatCurrency(totalDiscountAmount)}
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            color="text.main"
                                        >
                                            Final Amount
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight={600}
                                            color={theme.palette.error.main}
                                        >
                                            {formatCurrency(finalAmount)}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    mt={4}
                                >
                                    Payment Method Available
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        mt: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: 1,
                                            p: 1.5,
                                            borderRadius: 1,
                                            backgroundColor:
                                                theme.palette.error.main,
                                            color: theme.palette.common.white,
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight={600}
                                            letterSpacing={0.5}
                                        >
                                            Your Final Amount:{' '}
                                            {formatCurrency(finalAmount)}
                                        </Typography>
                                    </Box>
                                    <LoadingButton
                                        variant="contained"
                                        disabled={isPaymentProcess.value}
                                        startIcon={<ShoppingCart />}
                                        onClick={() => handleCreateInvoice()}
                                        loading={isPaymentProcess.value}
                                    >
                                        {isPaymentProcess.value
                                            ? 'Processing...'
                                            : 'PAY NOW'}
                                    </LoadingButton>
                                </Box>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </DashboardLayout>
        </>
    );
};

export default Payment;
